import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  TFunction,
  filterOptionsWithSynonyms,
  getCurrentReportingValues,
  getDatePickerDisabledRange,
  getEnvironment,
  getIsSubmissionPeriodOpen,
  useGetDataSubmissionDates,
  useGetFarmReportingYear,
  useGetFarms,
  useGetPracticeCrops,
  useGetPracticeSeedings,
  useGetRedzoneExceptions,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropFormValidationError,
  FieldListField,
  PracticeCrop,
  PracticeSlug,
  RedzoneException,
  WithInjectedName,
  ZERO_UUID,
} from "models";

import {
  AlertBanner,
  Flex,
  FlexPageSpin,
  Form,
  Icon,
  InputNumber,
  KlimRadioGroup,
  Modal,
  OptGroup,
  Option,
  RadioChangeEvent,
  RangePicker,
  RangerPickerValueType,
  Select,
  Spin,
  Tag,
  Tooltip,
} from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { cropHasDataToConfirm } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { InputRequirementConfig, isRequired, isRequiredForSaving, shouldShowInput } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { PRACTICE_TYPE } from "pages/app/field-timeline/components/FieldDetailsTimeline";

export type AddEditCropFormProps = {
  baselineYears: { [k in PracticeSlug]?: number[] };
  crop: CropFCPO;
  cropPeriodValidationErrors: CropFormValidationError[];
  disableTillageAndSeedingInputs: boolean;
  field: FieldListField | null;
  onCropDelete: () => Promise<void>;
  onCropSelectionChanged: (crop: PracticeCrop) => void;
  year: number;
  errors?: CropFormValidationError[];
  recommendedSeedingDate?: string;
  recommendedHarvestDate?: string;
  optionalInputsAreShown: boolean;
  requirementConfig: InputRequirementConfig | null;
  setShowDrawer?: (show: boolean) => void;
};

export const getFormTitle = (cropType: CROP_TYPE_FCPO) => {
  switch (cropType) {
    case CROP_TYPE_FCPO.MAIN:
      return "cropForm.mainCrop";
    case CROP_TYPE_FCPO.UNDERSOWING:
      return "cropForm.undersowing";
    case CROP_TYPE_FCPO.COVER:
      return "cropForm.coverCrop";
    default:
      return "cropForm.mainCrop";
  }
};

const ReviewTag = ({ t }: { t: TFunction }) => (
  <Tooltip title={t("cropForm.reviewDraftTooltip")}>
    <Flex align="center">
      <Tag colour="orange" text={t("cropForm.reviewDraft")} />
    </Flex>
  </Tooltip>
);

export const AddEditCropForm = forwardRef<HTMLDivElement, AddEditCropFormProps>(
  (
    {
      setShowDrawer,
      baselineYears,
      crop,
      cropPeriodValidationErrors,
      disableTillageAndSeedingInputs,
      field,
      onCropDelete,
      onCropSelectionChanged,
      recommendedSeedingDate,
      recommendedHarvestDate,
      optionalInputsAreShown,
      year,
      requirementConfig,
    }: AddEditCropFormProps,
    ref
  ) => {
    const { t } = useTranslation("allFieldView");
    const navigate = useNavigate();
    const location = useLocation();
    const { isNewCropMix, selectedRecommendationItemOnTimeline, showDrawerFromCMC, setIsNewCropMix, setShowDrawerFromCMC } = useCropMixComposerContext();
    const isSideCropRecommendation = selectedRecommendationItemOnTimeline?.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION;
    const CROP = "crop";
    const isAdmin = getEnvironment().admin;

    const { isSubmissionFeatureEnabled, submissionStatusValue } = useSubmissionStatus();

    const cropPracticesQuery = useGetPracticeCrops({ regionId: field?.regionId });
    const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
    const [selectedCropId, setSelectedCropId] = useState<string>(() => crop.cropId);
    const [isResidueProduced, setIsResidueProduced] = useState<boolean | null>(() => (crop.cropId !== "" ? crop.hasResidue : null));
    const [mandatory, setMandatory] = useState<boolean | null>(crop.mandatory ?? null);
    const [showCropDeletionModal, setShowCropDeletionModal] = useState<boolean>(false);
    const [seedingStartDatePickerKey, setSeedingStartDatePickerKey] = useState(0);
    const [harvestStartDatePickerKey, setHarvestStartDatePickerKey] = useState(0);
    const [showReviewTag, setShowReviewTag] = useState(false);
    const [cropsToDisplay, setCropsToDisplay] = useState<Array<WithInjectedName<PracticeCrop>> | undefined>([]);
    const form = Form.useFormInstance();
    const { data: redzoneExceptions } = useGetRedzoneExceptions();
    const { currentFarm } = useGetFarms();
    const { data: activeFarmReportingYear, isLoading: isFarmReportingYearLoading } = useGetFarmReportingYear(currentFarm?.id || null);
    const { data: dataSubmissionDates } = useGetDataSubmissionDates();

    const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
    const { endDate: reportingPeriodEnd } = getCurrentReportingValues(activeFarmReportingYear || moment().year(), currentFarm?.carbonProgram);
    const harvestDateRange = Form.useWatch([CROP, "harvestDateRange"], form);
    const harvestEndDate = harvestDateRange?.[1]; // Extract end date from range
    const isDraft = harvestEndDate ? moment(harvestEndDate).isAfter(reportingPeriodEnd) : false;

    // Compare the baseline years from the carbon runner to the year of the selected crop
    // in order to show (or not) the baseline tag
    const isBaseline = crop.year ? baselineYears?.crops?.includes(crop.year) : baselineYears?.crops?.includes(year);

    const getCropsIncludingPrevSelectedDisabledCrop = () => {
      const crops = cropPracticesQuery.data?.filter((crop) => crop.enabled);
      if (crop.cropId) {
        const selectedCrop = cropPracticesQuery.data?.find((cropItem) => cropItem.id === crop.cropId);
        if (selectedCrop && !selectedCrop.enabled) {
          crops?.push(selectedCrop);
        }
      }
      return crops;
    };

    const isSubmissionPeriodOpen = useMemo(
      () => getIsSubmissionPeriodOpen(dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate),
      [dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate]
    );

    useEffect(() => {
      const crops = getCropsIncludingPrevSelectedDisabledCrop();
      setCropsToDisplay(crops);
      const selectedCrop = crops?.find((crop) => crop.id === selectedCropId);
      if (!selectedCrop) return;
      onCropSelectionChanged(selectedCrop);
    }, [cropPracticesQuery.data, selectedCropId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      // Force the DatePicker to re-evaluate defaultValue by remounting it whenever recommendedSeedingDate or recommendedHarvestDate changes.
      // Since defaultValue is only used for the initial mount and does not update dynamically.

      const seedingDateRangeIsNull = (form.getFieldValue([CROP, "seedingDateRange"]) as RangerPickerValueType).every((item) => item == null);
      const harvestDateRangeIsNull = (form.getFieldValue([CROP, "harvestDateRange"]) as RangerPickerValueType).every((item) => item == null);

      if (seedingDateRangeIsNull) {
        setSeedingStartDatePickerKey((prevState) => prevState + 1);
      }
      if (harvestDateRangeIsNull) {
        setHarvestStartDatePickerKey((prevState) => prevState + 1);
      }
    }, [recommendedSeedingDate, recommendedHarvestDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResidueProducedChange = useCallback((e: RadioChangeEvent) => {
      setIsResidueProduced(!!e.target.value);
    }, []);

    // Fix the date format constants
    const getDatePickerDisabledDate = useCallback(
      (crop: CropFCPO, current: moment.Moment): boolean => {
        if (!crop.isConfirmed || !crop.year || isAdmin) return false;
        const lastSubmissionDate = submissionStatusValue?.lastSubmissionDate ? moment(submissionStatusValue?.lastSubmissionDate) : null;
        const { startDate: cropReportingPeriodStartDate, endDate: cropReportingPeriodEndDate } = getCurrentReportingValues(
          crop.year,
          currentFarm?.carbonProgram
        );

        // Guard against undefined dates
        if (!cropReportingPeriodStartDate || !cropReportingPeriodEndDate) return false;

        // if the submission date is after the reporting period end date, the dates are considered confirmed and cannot be changed
        if (!!lastSubmissionDate && lastSubmissionDate?.isValid() && lastSubmissionDate.isAfter(cropReportingPeriodEndDate, "day")) return true;

        // if there's no submission date, we allow the entire reporting period
        if (!lastSubmissionDate || !lastSubmissionDate?.isValid()) {
          return getDatePickerDisabledRange({
            current,
            startDate: cropReportingPeriodStartDate.format("MM/DD/YYYY"),
            endDate: cropReportingPeriodEndDate.format("MM/DD/YYYY"),
          });
        }

        // if the submission date is before the reporting period end date, we allow the dates to be changed within the submission date and the reporting period end date
        return getDatePickerDisabledRange({
          current,
          startDate: lastSubmissionDate.format("MM/DD/YYYY"),
          endDate: cropReportingPeriodEndDate.format("MM/DD/YYYY"),
        });
      },
      [currentFarm?.carbonProgram, submissionStatusValue?.lastSubmissionDate, isAdmin]
    );

    const seedingType = crop.seedingPracticeType || null;
    const selectedCropData = cropsToDisplay?.find((crop) => crop.id === selectedCropId);

    // the date inputs should be disabled if the crop is confirmed and either:
    // - there is a submission date and the selected crop date is before the last submission date OR
    // - there is no submission date and the crop year is not the one immediately before the active farm reporting year
    const isCropSeedingDateEditDisabled =
      !isAdmin &&
      !!crop.isConfirmed &&
      ((!!submissionStatusValue?.lastSubmissionDate && moment(crop.seedingStartDate).isBefore(submissionStatusValue?.lastSubmissionDate)) ||
        (!!activeFarmReportingYear && !!crop.year && crop.year < activeFarmReportingYear - 1));
    const isCropHarvestDateEditDisabled =
      !isAdmin &&
      !!crop.isConfirmed &&
      ((!!submissionStatusValue?.lastSubmissionDate && moment(crop.harvestEndDate).isBefore(submissionStatusValue?.lastSubmissionDate)) ||
        (!!activeFarmReportingYear && !!crop.year && crop.year < activeFarmReportingYear - 1));

    const handleEditMix = () => {
      if (!location.pathname.includes("cmc")) {
        navigate(`${location.pathname.replace(/\/$/, "")}/cmc/${crop.cropMixId}${location.search}`);
      }
      setShowDrawer?.(false);
      if (showDrawerFromCMC) {
        setShowDrawerFromCMC?.(false);
      }
    };

    useEffect(() => {
      if (isSideCropRecommendation) return;
      setIsNewCropMix(false);
    }, [setIsNewCropMix, isNewCropMix, isSideCropRecommendation]);

    useEffect(() => {
      // Only show review draft tag after September and only if crop entry hasn't been updated since then.
      if (isSubmissionFeatureEnabled && isSubmissionPeriodOpen && cropHasDataToConfirm(crop, isSubmissionPeriodOpen)) {
        return setShowReviewTag(true);
      }
      return setShowReviewTag(false);
    }, [crop, isSubmissionFeatureEnabled, isSubmissionPeriodOpen]);

    if (isFarmReportingYearLoading) {
      return <FlexPageSpin />;
    }

    return (
      <Flex vertical gap="1rem" className="add-edit-crop-form-container">
        {crop.isConfirmed && !isAdmin && <p className="submitted-crop-message">{t("cropForm.submittedCropMessage")}</p>}
        <Flex className="add-edit-crop-form">
          <div className="form-container" data-testid="add-edit-crop-form">
            <Flex justify="space-between">
              <Flex gap="0.75rem">
                <h5 className="margin-bottom-100">{t(getFormTitle(crop.cropType))}</h5>
                {isDraft && (
                  <Tooltip title={t("cropForm.draftTooltip")} style={{ lineHeight: "normal" }}>
                    <Tag dataTestId="timeline-drawer-crop-form-draft-tag" className="info-tag-drawer" colour="grey" text={t("cropForm.draft")} />
                  </Tooltip>
                )}
                {isBaseline && (
                  <Tag dataTestId="timeline-drawer-crop-form-baseline-tag" className="info-tag-drawer" colour="grey" text={t("cropForm.baseline")} />
                )}
                {crop.cropMixId && (
                  <Flex className="edit-crop-mix-container margin-bottom-100" gap=".125rem" onClick={handleEditMix}>
                    <span className="edit-crop-mix-label">{t("cropForm.editMix")}</span>
                    <Icon className="edit-crop-mix-icon" iconName="klim/plant-ai" />
                  </Flex>
                )}
              </Flex>
              {crop.id && (!crop.isConfirmed || isAdmin) && !isNewCropMix && (
                <Icon
                  className="pointer"
                  dataTestId="add-edit-crop-form-top-crop-delete-icon"
                  iconName="tabler/trash"
                  onClick={() => setShowCropDeletionModal(true)}
                />
              )}
            </Flex>
            <Form.Item name={[CROP, "cropType"]} hidden={true} initialValue={crop.cropType}>
              {/* we need this to prevent an antd form error; Form.Item needs a valid child */}
              <div />
            </Form.Item>
            {isNewCropMix && (
              <Flex className="new-crop-mix-banner margin-bottom-100" vertical gap="0.5rem">
                <span className="new-crop-mix-banner-title paragraph-m-bold">{t("cropForm.newCropMixBanner.title")}</span>
                <span className="new-crop-mix-banner-description paragraph-m">{t("cropForm.newCropMixBanner.description")}</span>
              </Flex>
            )}
            {(shouldShowInput(requirementConfig?.cropName, optionalInputsAreShown) || crop) && (
              <>
                {/* @ts-expect-error need to convert to tsx */}
                <Select
                  dataTestId="add-edit-crop-form-top-crop-selection"
                  label={t("cropForm.cropName")}
                  name={[CROP, "cropId"]}
                  initialValue={crop.cropId ?? null}
                  value={selectedCropId}
                  onChange={(value) => setSelectedCropId(value)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOptionsWithSynonyms}
                  disabled={crop.isConfirmed && !isAdmin}
                  isLoading={cropPracticesQuery.isFetching ? <Spin size="small" /> : null}
                  rules={[
                    {
                      required: isRequiredForSaving(requirementConfig?.cropName),
                      message: t("cropForm.errorMessages.cropRequired"),
                    },
                  ]}
                >
                  {crop.cropType === CROP_TYPE_FCPO.MAIN &&
                    cropsToDisplay
                      ?.filter((crop) => crop.isMainCrop)
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      .map((option, index) => (
                        <Option data-testid={`crop-selection-option-main-${index}`} key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      ))}

                  {crop.cropType === CROP_TYPE_FCPO.UNDERSOWING &&
                    cropsToDisplay
                      ?.filter((crop) => crop.isUndersowing)
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      .map((option, index) => (
                        <Option data-testid={`crop-selection-option-undersowing-${index}`} key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      ))}

                  {crop.cropType === CROP_TYPE_FCPO.COVER &&
                    cropsToDisplay
                      ?.filter((crop) => crop.isCatchCrop)
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      .map((option, index) => (
                        <Option data-testid={`crop-selection-option-cover-${index}`} key={option.id} value={option.id}>
                          {option.name}
                        </Option>
                      ))}
                </Select>
              </>
            )}

            {isInventoryProgram && selectedCropData && !selectedCropData.isModelReady && (
              <AlertBanner
                type="normal"
                iconName="tabler/info-circle"
                dataTestId="crop-not-model-ready-alert"
                description={t("cropForm.cropNotModelReady")}
                className="margin-top-100"
              />
            )}

            {(shouldShowInput(requirementConfig?.dates, optionalInputsAreShown) || crop) && (
              <Flex gap="1rem" className="margin-top-100 margin-bottom-100">
                <Flex
                  vertical
                  gap="1rem"
                  className="date-picker-container"
                  // aria-disabled is used for unit testing
                  aria-disabled={isCropSeedingDateEditDisabled}
                  data-testid="seeding-date-range-picker-container"
                  data-crop-type={crop.cropType}
                >
                  <RangePicker
                    key={seedingStartDatePickerKey}
                    data-field-id="seedingDateRange"
                    dataTestId={`add-edit-crop-form-seeding-date-range-${crop.cropName}`}
                    initialValue={[
                      crop.id ? (crop.seedingStartDate ? moment(crop.seedingStartDate) : null) : null,
                      crop.id ? (crop.seedingEndDate ? moment(crop.seedingEndDate) : null) : null,
                    ]}
                    defaultPickerValue={recommendedSeedingDate ? [moment(recommendedSeedingDate), moment(recommendedSeedingDate)] : [null, null]}
                    format={["DD.MM.YYYY"]}
                    label={t("cropForm.seedingDateRange")}
                    name={[CROP, "seedingDateRange"]}
                    rules={[
                      {
                        validator: (_, value: moment.Moment[] | null) => {
                          if (!isRequiredForSaving(requirementConfig?.dates)) return Promise.resolve();
                          if (value?.[0] != null) return Promise.resolve();
                          return Promise.reject(new Error(t("cropForm.errorMessages.seedingDateRequired")));
                        },
                      },
                    ]}
                    status={cropPeriodValidationErrors.find((error) => error.fieldId === "seedingStartDate") ? "error" : undefined}
                    // Once submitted, the seeding date for crops can still be changed within the respective farming year (only if the crop seeding date come after the submission date)
                    disabledDate={(current) => getDatePickerDisabledDate(crop, current)}
                    disabled={isCropSeedingDateEditDisabled}
                  />
                </Flex>
                <Flex
                  vertical
                  gap="1rem"
                  className="date-picker-container"
                  // aria-disabled is used for unit testing
                  aria-disabled={isCropHarvestDateEditDisabled}
                  data-testid="harvest-date-range-picker-container"
                  data-crop-type={crop.cropType}
                >
                  <RangePicker
                    key={harvestStartDatePickerKey}
                    data-field-id="harvestDateRange"
                    dataTestId={`add-edit-crop-form-harvest-date-range-${crop.cropName}`}
                    format={["DD.MM.YYYY"]}
                    initialValue={[
                      crop.id ? (crop.harvestStartDate ? moment(crop.harvestStartDate) : null) : null,
                      crop.id ? (crop.harvestEndDate ? moment(crop.harvestEndDate) : null) : null,
                    ]}
                    defaultPickerValue={recommendedHarvestDate ? [moment(recommendedHarvestDate), moment(recommendedHarvestDate)] : [null, null]}
                    name={[CROP, "harvestDateRange"]}
                    label={
                      <Flex align="center">
                        {showReviewTag && <ReviewTag t={t} />}
                        {t("cropForm.harvestDateRange")}
                      </Flex>
                    }
                    rules={[
                      {
                        validator: (_, value: moment.Moment[] | null) => {
                          if (!isRequiredForSaving(requirementConfig?.dates)) return Promise.resolve();
                          if (value?.[1] != null) return Promise.resolve();
                          return Promise.reject(new Error(t("cropForm.errorMessages.harvestDateRequired")));
                        },
                      },
                    ]}
                    status={cropPeriodValidationErrors.find((error) => error.fieldId === "harvestEndDate") ? "error" : undefined}
                    // Once submitted, the harvest date can still be changed within the respective farming year (only if the crop harvest date come after the submission date)
                    disabledDate={(current) => getDatePickerDisabledDate(crop, current)}
                    disabled={isCropHarvestDateEditDisabled}
                  />
                </Flex>
              </Flex>
            )}

            {(shouldShowInput(requirementConfig?.yield, optionalInputsAreShown) || crop.yieldTonsPerHa !== null) && (
              <InputNumber
                dataTestId="add-edit-crop-form-yield"
                name={[CROP, "yieldTonsPerHa"]}
                label={
                  <Flex align="center">
                    {showReviewTag && isRequired(requirementConfig?.yield) && <ReviewTag t={t} />}
                    <span className={isRequired(requirementConfig?.yield) ? "required" : ""}>{t("cropForm.yield")}</span>
                  </Flex>
                }
                rules={[{ required: isRequiredForSaving(requirementConfig?.yield) }]}
                initialValue={crop.yieldTonsPerHa ?? null}
                step={0.01}
                min={0}
                suffix="t/ha"
                className="margin-top-100 margin-bottom-100"
                // if none of the dates are editable, it means the crop was created and harvested before submission, so the yield should be already confirmed
                disabled={isCropSeedingDateEditDisabled && isCropHarvestDateEditDisabled}
              />
            )}

            <div>
              {/* The residue input does not depend on the requirement configuration because it depends directly on which crop is selected */}
              {selectedCropData && selectedCropData.residue && (
                <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
                  <p>
                    {crop.cropType === CROP_TYPE_FCPO.COVER || crop.cropType === CROP_TYPE_FCPO.UNDERSOWING
                      ? t("cropForm.residueLeftSideCrop")
                      : t("cropForm.residueLeft")}
                  </p>
                  <Form.Item
                    name={[CROP, "hasResidue"]}
                    // Residue should always be required for saving
                    rules={[{ required: true, message: t("modules:bauEntryDrawer.crops.residueLeft.error") }]}
                    initialValue={isResidueProduced}
                  >
                    <KlimRadioGroup
                      className="residue-button"
                      size="large"
                      type="tertiary"
                      dataTestId="residue-result-toggle"
                      options={[
                        {
                          value: true,
                          label: t("cropForm.yes"),
                        },
                        {
                          value: false,
                          label: t("cropForm.no"),
                        },
                      ]}
                      value={isResidueProduced || false}
                      onChange={handleResidueProducedChange}
                      optionType="button"
                      buttonStyle="solid"
                      fullWidth
                      disabled={!!crop.isConfirmed && !isAdmin}
                    />
                  </Form.Item>
                </Flex>
              )}

              {(shouldShowInput(requirementConfig?.seedings, optionalInputsAreShown) || crop.seedingPracticeType) && (
                <>
                  {seedingPracticesQuery.isFetching ? (
                    <Flex justify="center">
                      <Spin />
                    </Flex>
                  ) : (
                    <div ref={ref}>
                      {/* @ts-expect-error need to convert to tsx */}
                      <Select
                        dataTestId="add-edit-crop-form-seeding-type-selection"
                        disabled={disableTillageAndSeedingInputs}
                        filterOption={filterOptionsWithSynonyms}
                        initialValue={seedingType}
                        label={
                          <>
                            {t("cropForm.seedingType")}
                            {!!baselineYears?.tillages?.includes(Number(crop.year)) && (
                              <Tag className="info-tag-drawer margin-left-50" colour="grey" text={t("cropForm.baseline")} />
                            )}
                          </>
                        }
                        optionFilterProp="children"
                        name={[CROP, "seedingPracticeType"]}
                        rules={[{ message: "", required: isRequiredForSaving(requirementConfig?.seedings) }]}
                        showSearch
                        value={seedingType}
                      >
                        <OptGroup>
                          {seedingPracticesQuery.data &&
                            seedingPracticesQuery.data
                              .sort((a, b) => a.name?.localeCompare(b.name))
                              .map((option, index) => (
                                <Option data-testid={`crop-selection-option-${index}`} key={option.id} value={option.id} disabled={option.id === ZERO_UUID}>
                                  {option.name}
                                </Option>
                              ))}
                        </OptGroup>
                      </Select>
                    </div>
                  )}
                </>
              )}
            </div>

            {field?.isInRedzone && crop.cropType === CROP_TYPE_FCPO.COVER && (
              <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
                <p>{t("cropForm.cropMandatory")}</p>
                <Form.Item
                  name={[CROP, "mandatory"]}
                  // Redzone inputs should always be required for saving if rendered
                  rules={[{ required: true, message: t("modules:bauEntryDrawer.crops.residueLeft.error") }]}
                  initialValue={mandatory}
                >
                  <KlimRadioGroup
                    className="residue-button"
                    size="large"
                    type="tertiary"
                    dataTestId="crop-mandatory-result-toggle"
                    options={[
                      {
                        value: true,
                        label: (
                          <Flex vertical justify="space-around">
                            <span>{t("cropForm.yes")}</span>
                          </Flex>
                        ),
                      },
                      {
                        value: false,
                        label: (
                          <Flex vertical justify="space-around">
                            <span>{t("cropForm.no")}</span>
                          </Flex>
                        ),
                      },
                    ]}
                    value={mandatory}
                    onChange={(e) => setMandatory(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    fullWidth
                    disabled={!!crop.isConfirmed && !isAdmin}
                  />
                </Form.Item>
              </Flex>
            )}
            {field?.isInRedzone && crop.cropType === CROP_TYPE_FCPO.COVER && mandatory === false && (
              // @ts-expect-error need to convert to tsx
              <Select
                dataTestId="add-edit-crop-form-redzone-exception-type-selection"
                disabled={crop.redzoneExceptionId === null ? false : crop.isConfirmed && !isAdmin}
                filterOption={filterOptionsWithSynonyms}
                initialValue={crop.redzoneExceptionId ?? null}
                label={t("cropForm.whyNotMandatory")}
                optionFilterProp="children"
                name={[CROP, "redzoneExceptionId"]}
                // Redzone inputs should always be required for saving if rendered
                rules={[{ message: "", required: true }]}
                showSearch
              >
                <OptGroup>
                  {redzoneExceptions?.map((option: RedzoneException, index: number) => (
                    <Option data-testid={`crop-selection-option-${index}`} key={option.id} value={option.id}>
                      {option.description}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            )}
          </div>
          <Modal
            dataTestId="crop-form-delete"
            cancelText={t("cropForm.cropDeletionModal.cancel")}
            description={t("cropForm.cropDeletionModal.description")}
            onCancel={() => setShowCropDeletionModal(false)}
            okText={t("cropForm.cropDeletionModal.confirm")}
            onOk={async () => {
              setShowCropDeletionModal(false);
              await onCropDelete();
            }}
            showModal={showCropDeletionModal}
            title={t("cropForm.cropDeletionModal.title")}
          />
        </Flex>
      </Flex>
    );
  }
);
